import { cn } from '@/lib/utils';
import { formatPhone } from '@chiroup/core/functions/format';
import {
  CheckCircleIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import React, { useEffect, useRef, useState } from 'react';
import { hcVerbiage } from '../../common/verbiage';
import { usePatientPortal } from '../../contexts/PatientPortalContext';
import healthcomService from '../../services/healthcom.service';
import DejavooCardEntry, { DejavooCardEntryRef } from '../DejavooCardEntry';
import Modal from '../common/Modal';

const formatCurrency = (amount: number | undefined) => {
  const value = amount || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

type SavedCard = {
  key: string;
  processor: 'usaepay' | 'dejavoo';
  cardholder?: string;
  cardType?: string;
  lastFour?: string;
  isDefault?: boolean;
  // When true, the processing fee is waived on transactions paid with this
  // card (no toggle is presented to the patient).
  suppressProcessingFee?: boolean;
  chargeable: boolean;
};

const BalancePage: React.FC = () => {
  const { selectedPatient, language, refreshPatientData } = usePatientPortal();
  const balances = selectedPatient?.balances;
  const primaryColor = selectedPatient?.clinic?.primaryColor || '#77bd43';

  const totalBalance = balances?.balance || 0;
  const patientBalance = balances?.patientBalance || 0;
  const payorBalance = balances?.payorBalance || 0;
  const creditBalance = balances?.creditBalance || 0;

  const hasAnyBalance =
    totalBalance !== 0 || patientBalance !== 0 || payorBalance !== 0 || creditBalance !== 0;

  // Clinic-level "Allow patients to pay online through HealthCom"
  // toggle. Defaults to true (allowed) when the field is missing.
  // When false: the Pay panel and Add-card affordance are hidden
  // and the patient is shown a note pointing them at the clinic.
  // The backend re-checks this on every charge — frontend gating
  // is just for UX.
  const onlinePaymentsAllowed = selectedPatient?.clinic?.allowOnlinePayments !== false;

  // Cards fetched on mount via /me/{patientId}/payment-methods.
  // Empty until the call resolves.
  const [cards, setCards] = useState<SavedCard[]>([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardsError, setCardsError] = useState<string | null>(null);
  const [selectedCardKey, setSelectedCardKey] = useState<string | null>(null);
  // Add-card config (Dejavoo only for now). Populated alongside the
  // saved-cards list so we know whether to show the "Add a new
  // card" affordance and which widget to render.
  const [addCardConfig, setAddCardConfig] = useState<{
    processor: 'dejavoo';
    ecommToken: string;
  } | null>(null);
  // Clinic-level processing fee rate as a decimal (0.029 = 2.9%). 0 when
  // the clinic doesn't charge one. Returned by `listPaymentMethods`.
  const [processingFeeRate, setProcessingFeeRate] = useState<number>(0);
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [addCardSubmitting, setAddCardSubmitting] = useState(false);
  const [addCardError, setAddCardError] = useState<string | null>(null);
  const dejavooEntryRef = useRef<DejavooCardEntryRef | null>(null);

  // Editable amount, prefilled to the patient responsibility. Stored
  // as a string while the user is typing so they can clear it; we
  // parse it at submit time.
  const [amountInput, setAmountInput] = useState<string>('');
  useEffect(() => {
    setAmountInput(patientBalance > 0 ? patientBalance.toFixed(2) : '');
  }, [patientBalance]);

  // Fetch saved cards once we know the patient. Hidden errors are
  // soft — the patient can still try to pay, and the backend will
  // surface a useful message if no card is actually charged-able.
  useEffect(() => {
    if (!selectedPatient?.ID) return;
    let cancelled = false;

    const run = async () => {
      setCardsLoading(true);
      setCardsError(null);
      try {
        const res = await healthcomService.listPaymentMethods(selectedPatient.ID);
        if (cancelled) return;
        const list = res?.data || [];
        setCards(list);
        setAddCardConfig(res?.addCard || null);
        setProcessingFeeRate(Number(res?.processingFeeRate) || 0);
        // Prefer a chargeable default card → any chargeable card →
        // the first card on file (so non-chargeable cards still
        // display selected when they're all that's saved).
        const initial =
          list.find(c => c.isDefault && c.chargeable) || list.find(c => c.chargeable) || list[0];
        setSelectedCardKey(initial?.key ?? null);
      } catch (err: any) {
        if (cancelled) return;
        Sentry.captureException(err);
        setCardsError(err?.response?.data?.message || err?.message || 'Could not load cards.');
      } finally {
        if (!cancelled) setCardsLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [selectedPatient?.ID]);

  // Pay-down state.
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [paid, setPaid] = useState<{ amount: number } | null>(null);

  const parsedAmount = Number.parseFloat(amountInput);
  const amountValid = Number.isFinite(parsedAmount) && parsedAmount > 0;
  const amountTooHigh = amountValid && parsedAmount > patientBalance + 0.005;
  const selectedCard = cards.find(c => c.key === selectedCardKey) || null;
  const selectedChargeable = !!selectedCard?.chargeable;
  // Patient-side fee preview. The backend re-computes/charges authoritatively
  // and returns the final `amount` charged; this just shows the patient what
  // will hit their card. HealthCom rule: when the selected card is suppressed,
  // the fee is always off — no toggle is presented.
  const feeApplies = amountValid && processingFeeRate > 0 && !selectedCard?.suppressProcessingFee;
  const previewProcessingFee = feeApplies
    ? Math.round(parsedAmount * processingFeeRate * 100) / 100
    : 0;
  const previewTotal = (amountValid ? parsedAmount : 0) + previewProcessingFee;
  const canPay =
    !!selectedPatient?.ID &&
    patientBalance > 0 &&
    amountValid &&
    !amountTooHigh &&
    !!selectedCardKey &&
    selectedChargeable;

  const reloadCards = async (selectKey?: string) => {
    if (!selectedPatient?.ID) return;
    try {
      const res = await healthcomService.listPaymentMethods(selectedPatient.ID);
      const list = res?.data || [];
      setCards(list);
      setAddCardConfig(res?.addCard || null);
      setProcessingFeeRate(Number(res?.processingFeeRate) || 0);
      if (selectKey && list.some(c => c.key === selectKey)) {
        setSelectedCardKey(selectKey);
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const submitNewCard = async () => {
    if (!selectedPatient?.ID || !dejavooEntryRef.current) return;

    setAddCardSubmitting(true);
    setAddCardError(null);

    try {
      // FTD SDK tokenizes client-side — the PAN never crosses our
      // wire, only the resulting `paymentTokenId` + last4/brand.
      const result = await dejavooEntryRef.current.tokenize();

      if (!result) {
        // DejavooCardEntry already surfaced an error via onError →
        // setAddCardError below, so just bail.
        return;
      }

      const cardholderName =
        `${result.cardData.cardholderFirstName} ${result.cardData.cardholderLastName}`.trim();

      const res = await healthcomService.addPaymentMethod({
        patientId: selectedPatient.ID,
        paymentTokenId: result.paymentTokenId,
        cardholderName: cardholderName || undefined,
        postalCode: result.cardData.postalCode || undefined,
        cardLastFour: result.cardData.cardLastFour,
        cardType: result.cardData.cardType,
      });

      const newKey = res?.data?.key;
      await reloadCards(newKey);
      setAddCardOpen(false);
    } catch (err: any) {
      Sentry.captureException(err);
      setAddCardError(
        err?.response?.data?.message ||
          err?.message ||
          (language === 'es'
            ? 'No pudimos guardar la tarjeta. Intente de nuevo.'
            : 'We couldn’t save that card. Please try again.')
      );
    } finally {
      setAddCardSubmitting(false);
    }
  };

  const submitPayment = async () => {
    if (!selectedPatient?.ID || !canPay) return;

    setPaying(true);
    setPayError(null);

    try {
      const res = await healthcomService.payBalance({
        patientId: selectedPatient.ID,
        amount: parsedAmount,
        payMethodKey: selectedCardKey || undefined,
      });
      await refreshPatientData();
      setPaid({ amount: res?.amount ?? parsedAmount });
    } catch (err: any) {
      Sentry.captureException(err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        (language === 'es'
          ? 'No pudimos procesar el pago. Intente de nuevo o contacte al consultorio.'
          : 'We couldn’t process that payment. Try again or contact the clinic.');
      setPayError(msg);
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {hcVerbiage.balance[language]}
        </h2>
        <p className="mt-1 text-sm text-gray-400 dark:text-gray-500 dark:text-gray-400">
          {hcVerbiage.balanceDescription[language]}
        </p>
      </div>

      {/* Total Balance Card — hidden entirely when the clinic has
          online payments turned off; those patients only see the
          "online payments unavailable" note below. */}
      {onlinePaymentsAllowed && (
        <div
          className="rounded-3xl p-6 text-white dark:brightness-90 dark:ring-1 dark:ring-white/10"
          style={{ backgroundColor: primaryColor }}
        >
          <p className="text-sm font-medium opacity-80">
            {language === 'es' ? 'Saldo Total' : 'Total Balance'}
          </p>
          <p className="mt-2 text-3xl font-bold">{formatCurrency(patientBalance)}</p>
          <p className="mt-1 text-xs opacity-60">
            {selectedPatient?.fname} {selectedPatient?.lname}
          </p>
        </div>
      )}

      {/* Balance Breakdown — currently the only row we surface to
          patients is "Patient Responsibility", and in practice it
          always equals the Total Balance card above (the breakdown
          collapses to a single value once payor/credit/unallocated
          are excluded). Hidden for now to avoid showing the same
          number twice. Restore when there's more than one row to
          show. */}
      {/* {hasAnyBalance && (
        <div className="portal-card divide-y divide-gray-100 dark:divide-white/5">
          <BalanceRow
            icon={UserIcon}
            primaryColor={primaryColor}
            label={language === 'es' ? 'Responsabilidad del Paciente' : 'Patient Responsibility'}
            description={language === 'es' ? 'Monto que usted debe' : 'Amount you owe'}
            amount={patientBalance}
          />
        </div>
      )} */}

      {/* Online payments are disabled clinic-wide — show an
          informational note in place of the Pay panel. The Total
          Balance card and contact line are hidden in this state. */}
      {patientBalance > 0 && !paid && !onlinePaymentsAllowed && (
        <div className="portal-card p-5 sm:p-6">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {language === 'es' ? 'Pagos en línea no disponibles' : 'Online payments unavailable'}
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {language === 'es'
              ? 'Esta clínica no acepta pagos a través de HealthCom en este momento.'
              : 'This clinic isn’t accepting payments through HealthCom right now.'}
            {selectedPatient?.clinic?.phone ? (
              <>
                {language === 'es' ? ' Llame al ' : ' Call '}
                <a
                  href={`tel:${selectedPatient.clinic.phone}`}
                  className="font-semibold"
                  style={{ color: primaryColor }}
                >
                  {formatPhone(selectedPatient.clinic.phone, true)}
                </a>
                {language === 'es' ? ' para pagar su saldo.' : ' to pay your balance.'}
              </>
            ) : language === 'es' ? (
              ' Contacte al consultorio para pagar su saldo.'
            ) : (
              ' Contact the clinic to pay your balance.'
            )}
          </p>
        </div>
      )}

      {/* Pay panel — only when the patient has actual responsibility.
          Insurance / credit-only states get nothing here. */}
      {patientBalance > 0 && !paid && onlinePaymentsAllowed && (
        <div className="portal-card p-5 sm:p-6 flex flex-col gap-5">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {language === 'es' ? 'Pagar mi saldo' : 'Pay your balance'}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {language === 'es'
                ? 'Aplicaremos el pago a sus facturas comenzando por la más antigua.'
                : 'We’ll apply your payment to your invoices starting with the oldest first.'}
            </p>
          </div>

          {/* Amount input */}
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {language === 'es' ? 'Monto' : 'Amount'}
            </span>
            <span className="relative">
              <span
                aria-hidden="true"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-base font-semibold text-gray-500 dark:text-gray-400"
              >
                $
              </span>
              <input
                type="text"
                inputMode="decimal"
                value={amountInput}
                onChange={e => setAmountInput(e.target.value.replace(/[^0-9.]/g, ''))}
                disabled={paying}
                className="w-full pl-7 pr-3 py-2.5 text-lg font-semibold tabular-nums rounded-xl bg-white dark:bg-white/5 ring-1 ring-gray-950/10 dark:ring-white/15 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[--ring] disabled:opacity-50"
                style={{ ['--ring' as any]: primaryColor } as React.CSSProperties}
                placeholder={patientBalance.toFixed(2)}
              />
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {language === 'es'
                ? `Saldo del paciente: ${formatCurrency(patientBalance)}`
                : `Patient balance: ${formatCurrency(patientBalance)}`}
            </span>
            {amountTooHigh && (
              <span className="text-xs text-red-600 dark:text-red-400">
                {language === 'es'
                  ? `El monto no puede ser mayor a ${formatCurrency(patientBalance)}.`
                  : `Amount can’t be more than ${formatCurrency(patientBalance)}.`}
              </span>
            )}
          </label>

          {/* Card picker */}
          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {language === 'es' ? 'Método de pago' : 'Payment method'}
            </span>
            {cardsLoading ? (
              <div className="rounded-xl px-4 py-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-white/5">
                {language === 'es' ? 'Cargando tarjetas…' : 'Loading cards…'}
              </div>
            ) : cards.length === 0 ? (
              <div className="rounded-xl px-4 py-3 text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-white/5 ring-1 ring-gray-950/5 dark:ring-white/10">
                {language === 'es'
                  ? 'No hay tarjetas guardadas. Agregue una nueva tarjeta para pagar.'
                  : 'No saved cards. Add a new card to pay.'}
                {cardsError && (
                  <span className="block mt-1 text-xs text-red-600 dark:text-red-400">
                    {cardsError}
                  </span>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {cards.map(card => {
                  const checked = card.key === selectedCardKey;

                  return (
                    <label
                      key={card.key}
                      className={`flex items-center gap-3 cursor-pointer rounded-xl px-4 py-3 ring-1 transition-colors ${
                        checked
                          ? 'ring-2 bg-[--tint]'
                          : 'ring-gray-950/10 dark:ring-white/15 bg-white dark:bg-white/5 hover:bg-gray-50 dark:hover:bg-white/10'
                      }`}
                      style={
                        {
                          ['--ring' as any]: primaryColor,
                          ['--tint' as any]: `${primaryColor}10`,
                          ...(checked ? ({ ['--tw-ring-color' as any]: primaryColor } as any) : {}),
                          borderColor: checked ? primaryColor : undefined,
                        } as React.CSSProperties
                      }
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={card.key}
                        checked={checked}
                        disabled={paying}
                        onChange={() => setSelectedCardKey(card.key)}
                        className="sr-only"
                      />
                      <span
                        className="size-9 rounded-xl flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${primaryColor}18`, color: primaryColor }}
                        aria-hidden="true"
                      >
                        <CreditCardIcon className="size-5" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {card.cardType
                            ? `${card.cardType.toUpperCase()} •••• ${card.lastFour || '••••'}`
                            : card.lastFour
                              ? `Card •••• ${card.lastFour}`
                              : 'Saved card'}
                        </span>
                        {card.cardholder && (
                          <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                            {card.cardholder}
                          </span>
                        )}
                        {!card.chargeable && (
                          <span className="block text-xs mt-0.5 text-amber-600 dark:text-amber-400">
                            {language === 'es'
                              ? 'Solo se puede usar en el consultorio'
                              : 'In-clinic use only'}
                          </span>
                        )}
                      </span>
                      {checked && (
                        <CheckCircleIcon
                          className="size-5 shrink-0"
                          style={{ color: primaryColor }}
                          aria-hidden="true"
                        />
                      )}
                    </label>
                  );
                })}
              </div>
            )}

            {/* Add-new-card affordance. Only rendered when the
                clinic actually supports portal-side card adds — the
                listing endpoint returns `addCard` config when so. */}
            {addCardConfig && (
              <button
                type="button"
                onClick={() => {
                  setAddCardError(null);
                  setAddCardOpen(true);
                }}
                className="self-start mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                <PlusIcon className="size-4" />
                {language === 'es' ? 'Agregar nueva tarjeta' : 'Add a new card'}
              </button>
            )}
          </div>

          {selectedCard && !selectedCard.chargeable && (
            <p className="text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 rounded-xl px-3 py-2">
              {language === 'es'
                ? 'Esta tarjeta solo se puede usar en el consultorio. Contacte a la clínica para pagar por teléfono.'
                : 'This saved card can only be used at the clinic. Please contact the clinic to pay over the phone.'}
            </p>
          )}

          {feeApplies && (
            <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-white/5 rounded-xl px-3 py-2 flex flex-col gap-1">
              <div className="flex justify-between">
                <span>{language === 'es' ? 'Subtotal' : 'Subtotal'}</span>
                <span className="tabular-nums">{formatCurrency(parsedAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span>
                  {language === 'es'
                    ? `Tarifa de procesamiento (${(processingFeeRate * 100).toFixed(1)}%)`
                    : `Processing fee (${(processingFeeRate * 100).toFixed(1)}%)`}
                </span>
                <span className="tabular-nums">{formatCurrency(previewProcessingFee)}</span>
              </div>
              <div className="flex justify-between font-semibold text-gray-900 dark:text-gray-100">
                <span>{language === 'es' ? 'Total' : 'Total'}</span>
                <span className="tabular-nums">{formatCurrency(previewTotal)}</span>
              </div>
            </div>
          )}

          {payError && (
            <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 rounded-xl px-3 py-2">
              {payError}
            </p>
          )}

          <button
            type="button"
            onClick={submitPayment}
            disabled={!canPay || paying}
            className="self-stretch sm:self-start inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] transition-transform"
            style={{ backgroundColor: primaryColor }}
          >
            <CreditCardIcon className="size-4" />
            {paying
              ? language === 'es'
                ? 'Procesando…'
                : 'Processing…'
              : `${language === 'es' ? 'Pagar' : 'Pay'} ${formatCurrency(previewTotal)}`}
          </button>
        </div>
      )}

      {paid && (
        <div className="portal-card p-5 flex items-center gap-3">
          <span
            className="size-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${primaryColor}18`, color: primaryColor }}
            aria-hidden="true"
          >
            <CheckCircleIcon className="size-5" />
          </span>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {language === 'es'
                ? `Pago de ${formatCurrency(paid.amount)} aplicado.`
                : `Paid ${formatCurrency(paid.amount)}.`}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {language === 'es' ? '¡Gracias!' : 'Thanks!'}
            </p>
          </div>
        </div>
      )}

      {/* No Balance State */}
      {!hasAnyBalance && (
        <div className="portal-card px-6 py-16 text-center">
          <CreditCardIcon className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" />
          <h3 className="mt-4 text-base font-semibold text-gray-900 dark:text-gray-100">
            {language === 'es' ? 'Sin saldo pendiente' : 'No Outstanding Balance'}
          </h3>
          <p className="mt-2 text-sm text-gray-400 dark:text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
            {language === 'es'
              ? 'Su cuenta está al día. No tiene saldos pendientes.'
              : "You're all caught up! There are no outstanding balances on your account."}
          </p>
        </div>
      )}

      {/* Contact Info — hidden when online payments are off; the
          "online payments unavailable" note already gives patients
          the clinic's number to call. */}
      {onlinePaymentsAllowed && hasAnyBalance && selectedPatient?.clinic?.phone && (
        <div className="portal-card p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {language === 'es'
              ? 'Si tiene preguntas sobre su saldo, contáctenos al '
              : 'Questions about your balance? Contact us at '}
            <a
              href={`tel:${selectedPatient.clinic.phone}`}
              className="font-semibold"
              style={{ color: primaryColor }}
            >
              {formatPhone(selectedPatient.clinic.phone, true)}
            </a>
          </p>
        </div>
      )}

      {/* Add-card modal. Mounts the existing FTD-SDK card entry so
          the patient can save a new Dejavoo card from the portal.
          Tokenization happens client-side; only the resulting
          `paymentTokenId` + last4/brand cross our wire. */}
      <Modal
        isOpen={addCardOpen && addCardConfig?.processor === 'dejavoo'}
        close={() => {
          if (addCardSubmitting) return;
          setAddCardOpen(false);
          setAddCardError(null);
        }}
        size="default"
        showCloseButton
        disableClickOutsideClose={addCardSubmitting}
      >
        {/* No `overflow-y-auto` here — it forces `overflow-x: hidden`,
            which clips the input focus ring. Radix's DialogContent
            already constrains height + scrolls when content is taller
            than the viewport. */}
        <div className="flex flex-col gap-4">
          <div className="pr-8">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {language === 'es' ? 'Agregar nueva tarjeta' : 'Add a new card'}
            </h3>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {language === 'es'
                ? 'Se guardará para pagos futuros.'
                : 'We’ll save it for future payments.'}
            </p>
          </div>

          {addCardConfig?.processor === 'dejavoo' && (
            <DejavooCardEntry
              ref={dejavooEntryRef}
              ecommToken={addCardConfig.ecommToken}
              onTokenReceived={() => {
                /* `tokenize()` resolves with the same data; nothing else needed here */
              }}
              onError={msg => setAddCardError(msg)}
              isSubmitting={addCardSubmitting}
              defaultCardholderName={
                selectedPatient
                  ? `${selectedPatient.fname || ''} ${selectedPatient.lname || ''}`.trim()
                  : ''
              }
              showSaveCard={false}
            />
          )}

          {addCardError && (
            <p className="flex items-start gap-2 text-xs text-red-600 dark:text-red-400">
              <ExclamationTriangleIcon className="size-4 shrink-0 mt-px" aria-hidden="true" />
              <span>
                {/^DECLINE$/i.test(addCardError.trim())
                  ? language === 'es'
                    ? 'Tarjeta rechazada. Pruebe con otra tarjeta.'
                    : 'Card was declined — try a different one.'
                  : addCardError}
              </span>
            </p>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => {
                if (addCardSubmitting) return;
                setAddCardOpen(false);
                setAddCardError(null);
              }}
              disabled={addCardSubmitting}
              className="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 px-2.5 py-1.5 rounded-md disabled:opacity-50"
            >
              {language === 'es' ? 'Cancelar' : 'Cancel'}
            </button>
            <button
              type="button"
              onClick={submitNewCard}
              disabled={addCardSubmitting}
              className="inline-flex items-center justify-center rounded-full px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-50 disabled:cursor-progress active:scale-[0.98] transition-transform"
              style={{ backgroundColor: primaryColor }}
            >
              {addCardSubmitting
                ? language === 'es'
                  ? 'Guardando…'
                  : 'Saving…'
                : language === 'es'
                  ? 'Guardar tarjeta'
                  : 'Save card'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const BalanceRow: React.FC<{
  icon: React.ElementType;
  primaryColor: string;
  label: string;
  description: string;
  amount: number;
  isCredit?: boolean;
}> = ({ icon: Icon, primaryColor, label, description, amount, isCredit }) => (
  <div className="flex items-center px-4 py-4 gap-4">
    <span
      className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
      style={{ backgroundColor: `${primaryColor}15` }}
    >
      <Icon className="h-5 w-5" style={{ color: primaryColor }} />
    </span>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{label}</p>
      <p className="text-xs text-gray-400 dark:text-gray-500 dark:text-gray-400">{description}</p>
    </div>
    <p
      className={cn(
        'text-sm font-bold tabular-nums',
        isCredit
          ? 'text-green-600'
          : amount > 0
            ? 'text-gray-900 dark:text-gray-100'
            : 'text-gray-400 dark:text-gray-500 dark:text-gray-400'
      )}
    >
      {formatCurrency(Math.abs(amount))}
    </p>
  </div>
);

export default BalancePage;
